<script setup lang="ts">
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import {
  Chart,
  ArcElement,
  PieController,
  Tooltip,
  Legend,
  ChartConfiguration,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { generateColors } from '@/utils/color';
import { ChartData } from '@/composables/useSectorStats';

Chart.register(ArcElement, PieController, Tooltip, Legend, ChartDataLabels);

const props = defineProps<{ stats: ChartData }>();

const canvas = ref<HTMLCanvasElement | null>(null);
let chart: Chart | null = null;

const isDark = ref(false);
let observer: MutationObserver | null = null;

const config = computed<ChartConfiguration<'pie'>>(() => {
  const { topPie } = props.stats;
  const colors = generateColors(topPie.sectors.length);
  const total = topPie.scores.reduce((a, b) => a + b, 0);

  return {
    type: 'pie',
    data: {
      labels: topPie.sectors,
      datasets: [
        {
          data: topPie.scores,
          backgroundColor: colors,
          borderColor: isDark.value ? 'rgb(31 41 55)' : '#fff',
          borderWidth: 2,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        datalabels: {
          display: true,
          color: '#fff',
          font: { size: 13, weight: 'bold' as const },
          formatter: (value: number, ctx) => {
            if (!total) return '';
            const pct = ((value / total) * 100).toFixed(1);
            const label = (ctx.chart.data.labels?.[ctx.dataIndex] as string) ?? '';
            return `${label}\n${pct}%`;
          },
        },
        legend: {
          position: 'right' as const,
          labels: {
            color: isDark.value ? '#e2e8f0' : '#334155',
            padding: 12,
            font: { size: 12 },
          },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const value = ctx.parsed as number;
              const pct = total ? ((value / total) * 100).toFixed(1) : '0';
              return `${ctx.label}: ${value} 分 (${pct}%)`;
            },
          },
        },
      },
    },
  };
});

function render() {
  if (!canvas.value) return;
  if (chart) {
    chart.data = config.value.data;
    chart.options = config.value.options as any;
    chart.update('none');
    return;
  }
  chart = new Chart(canvas.value, config.value);
}

onMounted(() => {
  isDark.value = document.documentElement.classList.contains('dark');
  render();

  observer = new MutationObserver(() => {
    isDark.value = document.documentElement.classList.contains('dark');
    render();
  });
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
});

onBeforeUnmount(() => {
  chart?.destroy();
  chart = null;
  observer?.disconnect();
});

watch(config, render, { deep: true });
</script>

<template>
  <section class="card">
    <h2 class="mb-2 flex items-center gap-2 text-base font-semibold">
      <span class="h-1.5 w-1.5 rounded-full" style="background: rgb(var(--color-primary));"></span>
      板块权重分布
    </h2>
    <p class="mb-3 text-xs text-ink-muted">近 2 个月前 5 大板块</p>
    <div v-if="stats.topPie.sectors.length === 0" class="flex h-[300px] items-center justify-center text-ink-subtle">
      暂无数据，先添加第一条记录
    </div>
    <div v-else class="h-[320px]">
      <canvas ref="canvas"></canvas>
    </div>
  </section>
</template>
